import React from 'react';
import type {
  AfcSubmissionCandidateOpportunity,
  BioRenderableCandidate,
} from '@axiom/validation';

import {
  CandidateReputationDoNotShowBanner,
  doNotShowBannerNames,
} from '../CandidateReputationDoNotShowBanner/CandidateReputationDoNotShowBanner';
import { CandidateAccolades } from '../CandidateAccolades/CandidateAccolades';
import { CandidatePerformanceFeedback } from '../CandidatePerformanceFeedback/CandidatePerformanceFeedback';
import { Gutter } from '../../layout/Gutter/Gutter';
import { Layout } from '../../layout/Layout/Layout';
import { Paragraph } from '../../content/Paragraph/Paragraph';
import { ParagraphHeader } from '../../content/ParagraphHeader/ParagraphHeader';
import { SmallHeader } from '../../content/SmallHeader/SmallHeader';
import { Well } from '../../layout/Well/Well';
import { ReadonlyTextarea } from '../../form/Textarea/ReadonlyTextarea';

export type CandidateReputationPdfDisplayProps = {
  candidate: BioRenderableCandidate;
  showCandidateReputation?: AfcSubmissionCandidateOpportunity['showCandidateReputation'];
  candidateReputationClarification?: AfcSubmissionCandidateOpportunity['candidateReputationClarification'];
};

export const CandidateReputationPdfDisplay = ({
  candidate,
  showCandidateReputation,
  candidateReputationClarification,
}: CandidateReputationPdfDisplayProps) => {
  const isCandidateReputationEnabledByAdmin = showCandidateReputation ?? true;
  // showReputation/candidateReputation are optional on BioRenderableCandidate (the
  // envoy CandidateBio omits them; this component is gated off for AHQ via isAhqBio
  // upstream). Coalesce undefined -> null so the values match the downstream prop
  // types; Candidate/AfcSubmissionCandidate always carry the field so this is a no-op
  // for them.
  const showReputation = candidate.showReputation ?? null;
  const candidateReputation = candidate.candidateReputation ?? null;
  const hasEnoughQualtricsData =
    candidateReputation?.displayCandidateReputation ?? false;
  const isCandidateReputationEnabledByTalent =
    showReputation === true || showReputation === null;

  const setDisplay = () => {
    if (
      hasEnoughQualtricsData &&
      isCandidateReputationEnabledByAdmin &&
      isCandidateReputationEnabledByTalent
    ) {
      return (
        <Gutter top="24px">
          <CandidatePerformanceFeedback
            candidateReputation={candidateReputation}
          />
        </Gutter>
      );
    }
    if (
      !hasEnoughQualtricsData &&
      isCandidateReputationEnabledByAdmin &&
      isCandidateReputationEnabledByTalent
    ) {
      return (
        <CandidateReputationDoNotShowBanner
          name={doNotShowBannerNames.limitedFeedbackDisplay}
        />
      );
    }

    if (
      isCandidateReputationEnabledByAdmin &&
      !isCandidateReputationEnabledByTalent
    ) {
      return (
        <CandidateReputationDoNotShowBanner
          name={doNotShowBannerNames.optedOutDisplay}
          candidate={candidate}
        />
      );
    }

    return null;
  };

  return (
    <div data-test="candidateReputationPdfDisplay">
      <SmallHeader>Axiom Performance</SmallHeader>
      <Gutter bottom="16px" />
      <Layout direction="vertical">
        <CandidateAccolades candidateReputation={candidateReputation} />
        {setDisplay()}
        {candidateReputationClarification?.submissionComments ? (
          <Gutter top="16px">
            <Well>
              <ParagraphHeader>
                Axiom has provided context you may find helpful
              </ParagraphHeader>
              <Paragraph>
                <ReadonlyTextarea
                  name="submissionComments"
                  value={candidateReputationClarification.submissionComments}
                />
              </Paragraph>
            </Well>
          </Gutter>
        ) : null}
      </Layout>
    </div>
  );
};
